import { batch, createSignal, type Setter } from "solid-js";
import type { HistoryResponseListItem } from "~/solidJs/entities/HistoryListEntity/api/HistoryApi";
import { FileApi } from "~/solidJs/shared/api/fileApi";
import {
	getAllImageIdsFromHistoryOutputs,
	getAllVideoIdsFromHistoryOutputs
} from "~/solidJs/shared/helpers/nodeHelpers";
import type { ITaskDoneStatusResponse } from "~/solidJs/shared/helpers/types";
import type { IDataType } from "~/solidJs/shared/helpers/types/INodeDefinition";
import { historyStore } from "~/solidJs/shared/models/globalStores";

type historyImageLink = {
	src: string;
	initHeight?: number;
	initWidth?: number;
};
export type historyImageLinks = historyImageLink[];
export type IPreparedHistoryItem = {
	getFileLink: historyImageLinks;
	setFileLink: Setter<historyImageLinks>;
	getIsFavorite: boolean;
	setIsFavorite: Setter<boolean>;
	getIsDisplayable: boolean;
	getIndexToDisplay: number;
	previewType: IDataType;
} & HistoryResponseListItem;
/**
 * @description used to construct proper history item from backend wrong response from task status endpoint e.g: /task-status-list
 */
export function constructHistoryItemFromTask(item: ITaskDoneStatusResponse) {
	// switch ids, backend returns values in wrong fields
	// [item.historyItem.id, item.historyItem.params.id] = [item.historyItem.params.id, item.historyItem.id]
	return item.historyItem;
}

/**
 * @description used to mutate history store when task is done
 */
export function mutateHistory(tasks: ITaskDoneStatusResponse[]) {
	const items: HistoryResponseListItem[] = [];
	for (const task of tasks) {
		if (!task.historyItem) {
			continue;
		}
		items.push(constructHistoryItemFromTask(task));
	}
	historyStore.setHistoryStore((prev) => [...prepareNewHistoryItems(items), ...prev]);
}
function constructPrepearedItem(
	historyItem: HistoryResponseListItem,
	previewType: IDataType = "image"
) {
	const [getFileLink, setFileLink] = createSignal<historyImageLinks>([]);
	const [getIsFavorite, setIsFavorite] = createSignal<boolean>(historyItem.is_favorite);
	const preparedItem: IPreparedHistoryItem = {
		...historyItem,
		get getFileLink() {
			return getFileLink();
		},
		get getIsFavorite() {
			return getIsFavorite();
		},
		getIsDisplayable: true,
		setIsFavorite,
		setFileLink,
		getIndexToDisplay: 0,
		previewType
	};
	return preparedItem;
}

export function prepareNewHistoryItems(
	historyItems: Array<HistoryResponseListItem>
): IPreparedHistoryItem[] {
	const fileIdsMap: Record<number, number> = {};
	const preparedHistoryItems: IPreparedHistoryItem[] = [];
	for (let i = 0; i < historyItems.length; i++) {
		const historyItem = historyItems[i];
		const historyImageIds = getAllImageIdsFromHistoryOutputs(historyItem.params.outputs);
		const historyVideoIds = getAllVideoIdsFromHistoryOutputs(historyItem.params.outputs);

		// Handle images
		if (historyImageIds.length > 0) {
			for (let i = 0; i < historyImageIds.length; i++) {
				const fileId = historyImageIds[i];
				fileIdsMap[fileId] = preparedHistoryItems.length;
				const constructedItem = constructPrepearedItem(historyItem, "image");
				constructedItem.getIndexToDisplay = i;
				preparedHistoryItems.push(constructedItem);
			}
			continue;
		}

		// Handle videos
		if (historyVideoIds.length > 0) {
			for (let i = 0; i < historyVideoIds.length; i++) {
				const fileId = historyVideoIds[i];
				fileIdsMap[fileId] = preparedHistoryItems.length;
				const constructedItem = constructPrepearedItem(historyItem, "video");
				constructedItem.getIndexToDisplay = i;
				preparedHistoryItems.push(constructedItem);
			}
			continue;
		}

		// No displayable content
		const nonDisplayableItem = constructPrepearedItem(historyItem);
		nonDisplayableItem.getIsDisplayable = false;
		preparedHistoryItems.push(nonDisplayableItem);
	}
	fetchFilesAndMapToHistory(preparedHistoryItems, fileIdsMap);

	return preparedHistoryItems;
}

/**
 * @param preparedHistoryItems - list of history items that needs to be muated
 * @param fileIdsMap - map fileId to history item index
 */
const fetchFilesAndMapToHistory = async (
	preparedHistoryItems: IPreparedHistoryItem[],
	fileIdsMap: Record<number, number>
) => {
	const fileLinks = await FileApi.getLinksToAwsListByFileId(Object.keys(fileIdsMap).map(Number));
	batch(() => {
		for (const fileLinkItem of fileLinks) {
			// find to what history item file is releated to
			const index = fileIdsMap[fileLinkItem.file_id];
			const historyItem = preparedHistoryItems[index];
			if (!historyItem) continue;
			historyItem.setFileLink([
				{
					src: fileLinkItem.download_link,
					initHeight: fileLinkItem.height || undefined,
					initWidth: fileLinkItem.width || undefined
				}
			]);
		}
	});
};
